import React, { useState } from 'react';
import {
  Box,
  Card,
  CardMedia,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { type ProjectActivityItem } from '../services/api';
import eventPlaceholderImg from '../../img/project_essential_event_placeholder.png';

// Deterministic mock Unsplash event cover images
const MOCK_EVENT_IMAGES = [
  'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=500&auto=format&fit=crop&q=80', // Auditorium / Seminar
  'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=500&auto=format&fit=crop&q=80', // Workshop / Class
  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=500&auto=format&fit=crop&q=80', // Modern Skyscraper / Launch
  'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=500&auto=format&fit=crop&q=80', // Presentation / Chart
  'https://images.unsplash.com/photo-1511578314322-379afb476865?w=500&auto=format&fit=crop&q=80', // Conference / Networking
  'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=500&auto=format&fit=crop&q=80', // Briefing / Desk
];

export function getDeterministicMockImage(eventId: string): string {
  let hash = 0;
  const safeId = eventId || 'default-id';
  for (let i = 0; i < safeId.length; i++) {
    hash = safeId.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % MOCK_EVENT_IMAGES.length;
  return MOCK_EVENT_IMAGES[index];
}

function formatHtmlDescription(html: string): string {
  if (!html) return '';
  let formatted = html.replace(/<a\s+([^>]*?)>(.*?)<\/a>/gi, (_match, attrs) => {
    let newAttrs = attrs;
    if (!/target=/i.test(newAttrs)) {
      newAttrs += ' target="_blank"';
    }
    if (!/rel=/i.test(newAttrs)) {
      newAttrs += ' rel="noopener noreferrer"';
    }
    return `<a ${newAttrs} style="color: var(--color-accent); font-weight: 700; text-decoration: underline;">Open Link</a>`;
  });

  formatted = formatted.replace(/(href=")?(https?:\/\/[^\s<]+)/gi, (match, hrefPrefix, url) => {
    if (hrefPrefix) {
      return match;
    }
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: var(--color-accent); font-weight: 700; text-decoration: underline;">Open Link</a>`;
  });

  return formatted;
}

function formatEventDateTime(startStr: string | null, endStr: string | null) {
  if (!startStr) return { dateStr: '-', timeStr: '-' };
  const start = new Date(startStr);

  const pad = (n: number) => String(n).padStart(2, '0');
  const formatTime = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const formatDate = (d: Date) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const startDateStr = formatDate(start);
  const startTimeStr = formatTime(start);

  if (!endStr) {
    return {
      dateStr: startDateStr,
      timeStr: startTimeStr,
    };
  }

  const end = new Date(endStr);
  const endDateStr = formatDate(end);
  const endTimeStr = formatTime(end);

  if (startDateStr === endDateStr) {
    return {
      dateStr: startDateStr,
      timeStr: `${startTimeStr} - ${endTimeStr}`,
    };
  } else {
    return {
      dateStr: `${startDateStr} - ${endDateStr}`,
      timeStr: `${startTimeStr} - ${endTimeStr}`,
    };
  }
}

function formatGoogleCalendarDate(isoStr: string | null): string {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    const yyyy = d.getUTCFullYear();
    const mm = pad(d.getUTCMonth() + 1);
    const dd = pad(d.getUTCDate());
    const hh = pad(d.getUTCHours());
    const min = pad(d.getUTCMinutes());
    const ss = pad(d.getUTCSeconds());
    return `${yyyy}${mm}${dd}T${hh}${min}${ss}Z`;
  } catch {
    return '';
  }
}

function buildGoogleCalendarUrl(event: ProjectActivityItem): string {
  if (!event.start) return '#';
  
  const startFormatted = formatGoogleCalendarDate(event.start);
  let endFormatted = formatGoogleCalendarDate(event.end);
  
  if (!endFormatted) {
    const startDate = new Date(event.start);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
    endFormatted = formatGoogleCalendarDate(endDate.toISOString());
  }
  
  const dates = `${startFormatted}/${endFormatted}`;
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    dates,
    text: event.name || '',
    location: event.location || '',
    details: event.description || '',
  });
  
  return `https://www.google.com/calendar/event?${params.toString()}`;
}

interface EventPosterCardProps {
  event: ProjectActivityItem;
}

export function EventPosterCard({ event }: EventPosterCardProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { dateStr, timeStr } = formatEventDateTime(event.start, event.end);
  const coverUrl = event.thumbnail || eventPlaceholderImg;

  const handleCardClick = () => {
    setDetailsOpen(true);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Card
        onClick={handleCardClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          height: '100%',
          borderRadius: 3,
          border: '1px solid var(--color-border-strong)',
          bgcolor: 'var(--color-surface)',
          cursor: 'pointer',
          overflow: 'hidden',
          position: 'relative',
          transition: 'transform 150ms ease, border-color 150ms ease, box-shadow 150ms ease',
          '&:hover': {
            borderColor: 'var(--color-accent)',
            boxShadow: '0 8px 24px rgba(var(--color-accent-rgb), 0.12)',
            transform: 'translateY(-4px)',
          },
        }}
      >
        {/* Cover Photo */}
        <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', overflow: 'hidden' }}>
          <CardMedia
            component="img"
            image={coverUrl}
            alt={event.name}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {/* Overlay Calendar Badge */}
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 48,
              width: 'auto',
              px: 0.75,
              height: 48,
              borderRadius: 2,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid var(--color-border-strong)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              zIndex: 2,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: 'var(--color-accent)',
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                lineHeight: 1,
              }}
            >
              {event.start ? new Date(event.start).toLocaleString('en-US', { month: 'short' }) : 'EVT'}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 900,
                color: '#0A2540',
                lineHeight: 1,
                mt: 0.25,
                fontSize: '0.85rem',
                whiteSpace: 'nowrap',
              }}
            >
              {(() => {
                if (!event.start) return '-';
                const dStart = new Date(event.start);
                if (!event.end) return String(dStart.getDate());
                const dEnd = new Date(event.end);
                
                const startDay = new Date(dStart.getFullYear(), dStart.getMonth(), dStart.getDate());
                const endDay = new Date(dEnd.getFullYear(), dEnd.getMonth(), dEnd.getDate());
                
                if (startDay.getTime() === endDay.getTime()) {
                  return String(dStart.getDate());
                }
                return `${dStart.getDate()}-${dEnd.getDate()}`;
              })()}
            </Typography>
          </Box>
        </Box>

        {/* Card Body */}
        <Stack spacing={1.5} sx={{ p: 2, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 800,
              color: 'var(--color-text-strong)',
              lineHeight: 1.3,
              fontSize: '1rem',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              height: '2.6em', // Reserve space for 2 lines so card heights stay stable
            }}
            title={event.name}
          >
            {event.name}
          </Typography>

          <Stack spacing={0.75}>
            {/* Date Range */}
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarMonthIcon sx={{ fontSize: '1rem', color: 'var(--color-text-muted)' }} />
              <Typography
                variant="caption"
                sx={{ color: 'var(--color-text-muted)', fontWeight: 600, fontSize: '0.78rem' }}
              >
                {dateStr}
              </Typography>
            </Stack>

            {/* Time */}
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTimeIcon sx={{ fontSize: '1rem', color: 'var(--color-text-muted)' }} />
              <Typography
                variant="caption"
                sx={{ color: 'var(--color-text-muted)', fontWeight: 600, fontSize: '0.78rem' }}
              >
                {timeStr}
              </Typography>
            </Stack>

            {/* Location */}
            {event.location && (
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
                <LocationOnIcon sx={{ fontSize: '1rem', color: 'var(--color-text-muted)', mt: 0.25 }} />
                <Typography
                  variant="caption"
                  component="div"
                  dangerouslySetInnerHTML={{ __html: formatHtmlDescription(event.location) }}
                  sx={{
                    color: 'var(--color-text-muted)',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                    '& a': {
                      color: 'var(--color-accent)',
                      fontWeight: 700,
                      textDecoration: 'underline',
                    },
                  }}
                  title={event.location}
                />
              </Stack>
            )}
          </Stack>

          {event.description && (
            <Typography
              variant="body2"
              component="div"
              dangerouslySetInnerHTML={{ __html: formatHtmlDescription(event.description) }}
              sx={{
                color: 'var(--color-text-muted)',
                fontSize: '0.8rem',
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                height: '2.8em', // Stable height
                '& a': {
                  color: 'var(--color-accent)',
                  fontWeight: 700,
                  textDecoration: 'underline',
                },
              }}
            />
          )}

          {/* Flex spacer to push the button to the bottom of the card.
              MUI Stack's `spacing` prop applies `marginTop` to non-first
              children, which overrides `mt: 'auto'` on the button —
              this empty Box bypasses that by absorbing the slack. */}
          <Box sx={{ flex: 1 }} />

          <Button
            component="a"
            href={buildGoogleCalendarUrl(event)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            variant="outlined"
            size="small"
            startIcon={<CalendarMonthIcon />}
            fullWidth
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 999,
              borderColor: 'var(--color-border-strong)',
              color: 'var(--color-text-strong)',
              fontSize: '0.75rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                borderColor: 'var(--color-accent)',
                bgcolor: 'var(--color-accent-soft)',
              },
            }}
          >
            Add to Calendar
          </Button>
        </Stack>
      </Card>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            bgcolor: 'var(--color-surface)',
          },
        }}
      >
        {/* Banner Image */}
        <Box sx={{ position: 'relative', width: '100%', height: 240 }}>
          <CardMedia
            component="img"
            image={coverUrl}
            alt={event.name}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <IconButton
            onClick={() => setDetailsOpen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
              zIndex: 10,
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            {/* Title */}
            <Typography variant="h5" sx={{ fontWeight: 850, color: 'var(--color-text-strong)', lineHeight: 1.3 }}>
              {event.name}
            </Typography>

            {/* Quick Metadata */}
            <Stack spacing={1.25} sx={{ p: 2, bgcolor: 'var(--color-surface-elevated)', borderRadius: 2, border: '1px solid var(--color-border-strong)' }}>
              {/* Date */}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CalendarMonthIcon sx={{ color: 'var(--color-accent)', fontSize: '1.2rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                  {dateStr}
                </Typography>
              </Stack>

              {/* Time */}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <AccessTimeIcon sx={{ color: 'var(--color-accent)', fontSize: '1.2rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                  {timeStr}
                </Typography>
              </Stack>

              {/* Location */}
              {event.location && (
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <LocationOnIcon sx={{ color: 'var(--color-accent)', fontSize: '1.2rem', mt: 0.25 }} />
                  <Typography
                    variant="body2"
                    component="div"
                    dangerouslySetInnerHTML={{ __html: formatHtmlDescription(event.location) }}
                    sx={{
                      fontWeight: 700,
                      color: 'var(--color-text-strong)',
                      '& a': {
                        color: 'var(--color-accent)',
                        fontWeight: 800,
                        textDecoration: 'underline',
                        '&:hover': { color: 'var(--color-accent-strong)' },
                      },
                    }}
                  />
                </Stack>
              )}
            </Stack>

            {/* Description */}
            {event.description && (
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'var(--color-text-strong)' }}>
                  Description
                </Typography>
                <Typography
                  variant="body1"
                  component="div"
                  dangerouslySetInnerHTML={{ __html: formatHtmlDescription(event.description) }}
                  sx={{
                    color: 'var(--color-text-muted)',
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                    '& a': {
                      color: 'var(--color-accent)',
                      fontWeight: 800,
                      textDecoration: 'underline',
                      '&:hover': { color: 'var(--color-accent-strong)' },
                    },
                  }}
                />
              </Stack>
            )}

            {/* Dialog Footer Actions */}
            <Box
              sx={{
                pt: 2.5,
                borderTop: '1px solid var(--color-border-strong)',
                display: 'flex',
                gap: 1.5,
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setDetailsOpen(false)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 999,
                  color: 'var(--color-text-strong)',
                  borderColor: 'var(--color-border-strong)',
                  px: 3,
                  '&:hover': {
                    borderColor: 'var(--color-accent)',
                    bgcolor: 'var(--color-accent-soft)',
                  },
                }}
              >
                Close
              </Button>
              <Button
                component="a"
                href={buildGoogleCalendarUrl(event)}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={<CalendarMonthIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 999,
                  bgcolor: 'var(--color-accent)',
                  color: '#fff',
                  px: 3,
                  '&:hover': {
                    bgcolor: 'var(--color-accent-strong)',
                  },
                }}
              >
                Add to Calendar
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
